import Marquee from "react-fast-marquee";
import { Reveal, MaskedInView } from "../lib/anim";

const HER_POINTS = [
  "Slowing down",
  "Becoming present",
  "Noticing subtle sensations",
  "Receiving touch without rushing",
  "Communicating desires",
  "Communicating boundaries",
  "Feeling grounded during intimate moments",
  "Moving from mental activity toward embodied awareness",
  "Experiencing connection without feeling pressure to perform",
  "Creating space for pleasure, curiosity and exploration",
];

const HIM_POINTS = [
  "Staying present",
  "Slowing down",
  "Noticing subtle sensations",
  "Communicating openly",
  "Feeling emotionally connected",
  "Experiencing touch intentionally",
  "Moving between excitement and relaxation",
  "Feeling grounded and embodied",
  "Exploring intimacy without making everything about performance",
];

const RITUAL_LINES = [
  "Put the phone away.",
  "Dim the lights.",
  "Put on the music.",
  "Slow down.",
  "Breathe.",
  "Notice your body.",
  "Notice your partner.",
  "Let touch become communication.",
  "Let sensation become meditation.",
  "Let pleasure become presence.",
];

export function ForHerHim() {
  const Column = ({ title, points, testid }) => (
    <div data-testid={testid} className="border hairline-gold bg-white/[0.02] p-8 sm:p-10">
      <p className="ff-serif text-3xl font-light tracking-[0.08em] gold-text">{title}</p>
      <p className="mt-4 text-[12px] font-light leading-relaxed text-[#f7e7ce]/60 ff-sans">
        A greater capacity for nervous-system regulation can help create conditions supportive of:
      </p>
      <ul className="mt-6 space-y-3">
        {points.map((p, i) => (
          <Reveal key={p} delay={0.05 + i * 0.04} y={12}>
            <li className="flex items-start gap-3 text-[13px] font-light leading-relaxed text-[#f7e7ce]/80 ff-sans">
              <span className="mt-0.5 text-[10px] text-[#d4af37]">✦</span>
              {p}
            </li>
          </Reveal>
        ))}
      </ul>
    </div>
  );

  return (
    <section data-testid="for-her-him-section" className="bg-[#050505] px-6 py-28 sm:py-40">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center ff-serif font-light leading-[0.98] text-[#fdfbf7]">
          <MaskedInView>
            <span className="block text-[10vw] sm:text-6xl">PRESENCE CHANGES</span>
          </MaskedInView>
          <MaskedInView delay={0.15}>
            <span className="block text-[10vw] sm:text-6xl italic gold-text">THE EXPERIENCE.</span>
          </MaskedInView>
        </h2>
        <div className="mt-16 grid gap-6 md:grid-cols-2">
          <Reveal delay={0.1}><Column title="FOR HER" points={HER_POINTS} testid="for-her-card" /></Reveal>
          <Reveal delay={0.2}><Column title="FOR HIM" points={HIM_POINTS} testid="for-him-card" /></Reveal>
        </div>
        <Reveal delay={0.15} className="mt-10 text-center">
          <p data-testid="her-him-disclaimer" className="mx-auto max-w-2xl text-[11px] leading-relaxed text-[#f7e7ce]/45 ff-sans">
            These statements describe nervous-system regulation generally and are not claims that Pleasure Portal
            treats or prevents any medical condition.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function Ritual() {
  return (
    <section data-testid="ritual-section" className="relative bg-[#050505] px-6 py-28 sm:py-40">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(212,175,55,0.08), transparent 60%)" }} />
      <div className="relative mx-auto max-w-3xl text-center">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#f7e7ce]/50 ff-sans">Chapter 02 — The Practice</p>
        </Reveal>
        <h2 className="mt-8 ff-serif font-light leading-[0.95] text-[#fdfbf7]">
          <MaskedInView delay={0.1}>
            <span className="block text-[10vw] sm:text-6xl">TURN PLEASURE</span>
          </MaskedInView>
          <MaskedInView delay={0.22}>
            <span className="block text-[10vw] sm:text-6xl italic gold-text">INTO A RITUAL.</span>
          </MaskedInView>
        </h2>

        <div className="mt-20 space-y-6">
          {RITUAL_LINES.map((line, i) => (
            <MaskedInView key={line} delay={0.05}>
              <span className="ff-serif block text-2xl sm:text-3xl font-light text-[#f7e7ce]/70">
                <span className="mr-3 text-[10px] uppercase tracking-[0.3em] text-[#d4af37]/60 ff-sans align-middle">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {line}
              </span>
            </MaskedInView>
          ))}
        </div>

        <MaskedInView delay={0.15} className="mt-20">
          <span className="ff-serif block text-[11vw] sm:text-7xl font-light italic gold-text">ENTER THE PORTAL.</span>
        </MaskedInView>
      </div>
    </section>
  );
}

export function EditorialMarquee() {
  const items = ["Pleasure", "Presence", "Connection", "Sensation", "Ritual", "Breath", "Touch"];
  return (
    <div data-testid="editorial-marquee" className="border-y border-[#d4af37]/30 bg-[#f7e7ce] py-4 text-[#050505]">
      <Marquee speed={28} gradient={false} aria-hidden="true">
        {items.map((w) => (
          <span key={w} className="mx-10 ff-serif text-2xl font-light italic tracking-wide">
            {w} <span className="not-italic text-[#a8842c]">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export function SoloTogether() {
  const cards = [
    {
      n: "01",
      title: "SOLO",
      body: "Create intentional space to slow down, explore sensation and reconnect with your own body.",
    },
    {
      n: "02",
      title: "TOGETHER",
      body: "Bring more presence, curiosity, communication and intentionality into intimate moments with a consenting partner.",
    },
    {
      n: "03",
      title: "THE RITUAL",
      body: "Breath. Touch. Presence. Sensation. Turn an ordinary moment into an intentional experience.",
    },
  ];
  return (
    <section data-testid="solo-together-section" className="bg-[#050505] px-6 py-28 sm:py-40">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center ff-serif font-light leading-[0.98] text-[#fdfbf7]">
          <MaskedInView>
            <span className="block text-[9vw] sm:text-5xl">THERE IS NO</span>
          </MaskedInView>
          <MaskedInView delay={0.12}>
            <span className="block text-[9vw] sm:text-5xl">“RIGHT WAY”</span>
          </MaskedInView>
          <MaskedInView delay={0.24}>
            <span className="block text-[9vw] sm:text-5xl italic gold-text">TO FEEL.</span>
          </MaskedInView>
        </h2>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {cards.map((c, i) => (
            <Reveal key={c.n} delay={0.1 + i * 0.12}>
              <div
                data-testid={`ritual-card-${c.n}`}
                className="group relative h-full border hairline-champagne bg-white/[0.02] p-8 transition-all duration-500 hover:border-[#d4af37]/50 hover:bg-white/[0.04] hover:shadow-[0_0_50px_rgba(212,175,55,0.12)]"
              >
                <p className="ff-serif text-5xl font-light text-[#d4af37]/40 transition-colors duration-500 group-hover:text-[#d4af37]">{c.n}</p>
                <p className="mt-6 text-[12px] uppercase tracking-[0.35em] gold-text ff-sans font-medium">{c.title}</p>
                <p className="mt-4 text-[13px] font-light leading-relaxed text-[#f7e7ce]/70 ff-sans">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
